import React, { useEffect, useState } from 'react';
import { Ticket, Trophy, X, Check, Loader2, Clock, Banknote } from 'lucide-react';
import client from '../../lib/api';
import StatusBadge from '../../components/StatusBadge';
import { accountHook } from '../../lib/domHooks';

// Hazır spor kitabı (Flexrix / Betby) yayındayken kupon bizim ``coupons``
// tablomuza hiç yazılmaz: bahis sağlayıcıda durur, bize yalnızca para hareketi
// gelir. Bu ekran o kuponları ayrı bir uçtan okur, yoksa oyuncu oynadığı
// bahsi sitede hiçbir yerde göremezdi.
const hostedBadge = {
  pending: { l: 'Sonuç bekleniyor', c: 'var(--pusu-gold)', I: Clock },
  won: { l: 'Kazandı', c: '#22c55e', I: Check },
  half_won: { l: 'Yarı kazandı', c: '#22c55e', I: Check },
  lost: { l: 'Kaybetti', c: '#e11d48', I: X },
  half_lost: { l: 'Yarı kaybetti', c: '#e11d48', I: X },
  void: { l: 'İade', c: '#94a3b8', I: Banknote },
  cashed_out: { l: 'Cashout', c: '#eab308', I: Banknote },
};

// Aktif kuponlarda erken sonlandırma (cashout) teklifi gösterilir. Teklif
// bağlayıcı değildir: kabul anında sunucu yeniden hesaplar, tolerans dışına
// kaydıysa 409 ile taze teklif döner ve buton güncellenir.
const BetCoupons = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState({});      // coupon_id -> teklif
  const [busy, setBusy] = useState({});          // coupon_id -> istek sürüyor
  const [notice, setNotice] = useState(null);
  const [hosted, setHosted] = useState([]);

  const loadCoupons = async () => {
    try {
      const { data } = await client.get('/finance/coupons');
      const list = data.items || [];
      setItems(list);
      return list;
    } catch { return []; }
  };

  // Hazır spor kitabı kuponları ayrı bir uçtan gelir (bahis sağlayıcıda durur,
  // bize yalnızca para hareketi gelir) ama ekranda **tek liste** vardır:
  // oyuncunun sorusu "ne oynadım", kuponun hangi altyapıdan geldiği değil.
  useEffect(() => {
    client.get('/finance/sportsbook-bets')
      .then(({ data }) => setHosted(data.items || []))
      .catch(() => setHosted([]));
  }, []);

  useEffect(() => {
    (async () => {
      const list = await loadCoupons();
      setLoading(false);
      // Aktif kuponlar için teklifleri arka planda çek
      list.filter((c) => c.status === 'active').forEach(async (c) => {
        try {
          const { data } = await client.get(`/finance/coupons/${c.id}/cashout`);
          setQuotes((q) => ({ ...q, [c.id]: data }));
        } catch {}
      });
    })();
  }, []);

  const acceptCashout = async (coupon) => {
    const quote = quotes[coupon.id];
    if (!quote?.available || busy[coupon.id]) return;
    setBusy((b) => ({ ...b, [coupon.id]: true }));
    setNotice(null);
    try {
      const { data } = await client.post(`/finance/coupons/${coupon.id}/cashout`, {
        expected: quote.value,
      });
      setNotice({ type: 'ok', text: `Kupon ${Number(data.value).toFixed(2)} ₺ karşılığında kapatıldı.` });
      setQuotes((q) => { const n = { ...q }; delete n[coupon.id]; return n; });
      await loadCoupons();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.cashout) {
        // Teklif değişti: taze değeri göster, kullanıcı isterse tekrar dener
        setQuotes((q) => ({ ...q, [coupon.id]: detail.cashout }));
        setNotice({ type: 'warn', text: 'Cashout değeri değişti, güncel teklif yüklendi.' });
      } else {
        setNotice({ type: 'err', text: typeof detail === 'string' ? detail : 'Cashout işlemi yapılamadı.' });
      }
    } finally {
      setBusy((b) => ({ ...b, [coupon.id]: false }));
    }
  };

  const statusBadge = {
    active: { l: 'Beklemede', c: 'var(--pusu-gold)', I: Clock },
    won: { l: 'Kazandı', c: '#22c55e', I: Check },
    lost: { l: 'Kaybetti', c: '#e11d48', I: X },
    cashed_out: { l: 'Cashout', c: '#eab308', I: Banknote },
  };

  return (
    <div className="pusu-panel p-4 sm:p-6" {...accountHook('panel')}>
      <div className="flex items-center gap-3 mb-5" {...accountHook('page-header')}>
        <Ticket size={20} className="text-[var(--pusu-gold)]" />
        <h2 className="text-xl font-bold gold-text" {...accountHook('title')}>Bahis Kuponlarım</h2>
        <span className="ml-auto text-xs text-gray-400" {...accountHook('summary')}>{items.length + hosted.length} kupon</span>
      </div>
      {notice && (
        <div data-bhs-status={notice.type} {...accountHook('notice')} className={`mb-4 px-3 py-2 rounded text-sm ${notice.type === 'ok' ? 'bg-emerald-500/10 text-emerald-400' : notice.type === 'warn' ? 'bg-yellow-500/10 text-yellow-400' : 'bg-rose-500/10 text-rose-400'}`}>
          {notice.text}
        </div>
      )}
      {hosted.length > 0 && (
        <div className="space-y-3 mb-3" data-bhs-source="hosted" {...accountHook('list')}>
          {hosted.map((b) => {
            const s = hostedBadge[b.status] || hostedBadge.pending;
            const Ic = s.I;
            const settled = b.status !== 'pending';
            return (
              <div key={b.id} className="pusu-card p-3 sm:p-4" data-bhs-status={b.status} {...accountHook('row')}>
                <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-white/5" {...accountHook('header')}>
                  <Trophy size={14} className="shrink-0 text-[var(--pusu-gold)]" />
                  <span className="min-w-0 flex-1 text-xs text-gray-400">
                    {b.provider_label} • {b.placed_at ? new Date(b.placed_at).toLocaleString('tr-TR') : '-'}
                  </span>
                  <StatusBadge color={s.c} className="ml-auto inline-flex shrink-0 items-center gap-1 px-2 py-1 rounded text-[10px] font-bold">
                    <Ic size={10} /> {s.l}
                  </StatusBadge>
                </div>
                {(b.selections || []).length > 0 && (
                  <div className="py-2 space-y-1 border-b border-white/5" {...accountHook('legs')}>
                    {b.selections.map((leg, i) => (
                      <div key={i} className="flex flex-col gap-1.5 text-sm sm:flex-row sm:items-center sm:gap-2" {...accountHook('leg')}>
                        <span className="min-w-0 break-words leading-snug text-gray-400 sm:flex-1 sm:truncate">{leg.event || 'Maç bilgisi yok'}</span>
                        <div className="flex min-w-0 items-start gap-2 sm:ml-auto sm:items-center">
                          <span className="min-w-0 flex-1 leading-snug sm:flex-none sm:whitespace-nowrap sm:text-right">
                            {leg.market ? <>{leg.market}: <b>{leg.pick}</b></> : <b>{leg.pick}</b>}
                            {leg.score ? <span className="text-gray-500 ml-1">({leg.score})</span> : null}
                          </span>
                          {Number(leg.odds) > 0 && (
                            <span className="gold-text w-12 shrink-0 text-right font-bold">{Number(leg.odds).toFixed(2)}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-3 pt-2 text-center text-xs" {...accountHook('summary')}>
                  <div>
                    <div className="text-gray-500">{Number(b.odds) > 0 ? 'Toplam Oran' : 'Kupon No'}</div>
                    <div className="font-bold text-sm mt-0.5 truncate" title={b.bet_id}>
                      {Number(b.odds) > 0 ? Number(b.odds).toFixed(2) : b.bet_id}
                    </div>
                  </div>
                  <div><div className="text-gray-500">Miktar</div><div className="font-bold text-sm mt-0.5">{Number(b.stake).toFixed(2)} ₺</div></div>
                  <div>
                    <div className="text-gray-500">{b.status === 'void' ? 'İade' : 'Ödenen'}</div>
                    <div className="gold-text font-bold text-sm mt-0.5">
                      {Number(b.status === 'void' ? b.refund : b.payout).toFixed(2)} ₺
                    </div>
                  </div>
                </div>
                {!settled && !(b.selections || []).length && (
                  <div className="mt-2 text-center text-[11px] text-gray-500">
                    Kuponun ayrıntısı spor kitabı ekranındaki "Bahislerim" bölümündedir.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {loading ? (
        <div className="text-center py-10 text-gray-400" {...accountHook('loading')}><Loader2 className="inline animate-spin mr-2" size={16} /> Yükleniyor...</div>
      ) : items.length === 0 ? (
        hosted.length > 0 ? null : (
        <div className="text-center py-10 text-gray-500" {...accountHook('empty')}>Henüz bir kuponunuz yok. Spor sayfasından bahis oynayabilirsiniz.</div>
        )
      ) : (
        <div className="space-y-3" data-bhs-source="sports" {...accountHook('list')}>
          {items.map((c) => {
            const s = statusBadge[c.status] || statusBadge.active;
            const Ic = s.I;
            const quote = quotes[c.id];
            return (
              <div key={c.id} className="pusu-card p-3 sm:p-4" data-bhs-status={c.status} {...accountHook('row')}>
                <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-white/5" {...accountHook('header')}>
                  <Trophy size={14} className="shrink-0 text-[var(--pusu-gold)]" />
                  <span className="min-w-0 flex-1 text-xs text-gray-400">{c.picks.length} seçimli • {new Date(c.created_at).toLocaleString('tr-TR')}</span>
                  <StatusBadge color={s.c} className="ml-auto inline-flex shrink-0 items-center gap-1 px-2 py-1 rounded text-[10px] font-bold">
                    <Ic size={10} /> {s.l}
                  </StatusBadge>
                </div>
                <div className="py-2 space-y-1" {...accountHook('legs')}>
                  {c.picks.map((p, i) => (
                    <div key={i} className="flex flex-col gap-1.5 text-sm sm:flex-row sm:items-center sm:gap-2" {...accountHook('leg')}>
                      <span className="min-w-0 break-words leading-snug text-gray-400 sm:flex-1 sm:truncate">{p.home} - {p.away}</span>
                      <div className="flex min-w-0 items-start gap-2 sm:ml-auto sm:items-center">
                        <span className="min-w-0 flex-1 leading-snug sm:flex-none sm:whitespace-nowrap sm:text-right">
                          {p.provider === 'sportsbook'
                            ? <>{p.market_type === 'POINT_SPREAD' ? 'Handikap' : p.market_type === 'POINT_TOTAL' ? 'Toplam' : 'Maç Sonucu'}: <b>{p.label || p.pick_key}</b></>
                            : <>Maç Sonucu: <b>{p.pick_key}</b></>}
                          {p.score ? <span className="text-gray-500 ml-1">({p.score})</span> : null}
                        </span>
                        <span className="gold-text w-12 shrink-0 text-right font-bold">{Number(p.odd).toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-3 pt-2 border-t border-white/5 text-center text-xs" {...accountHook('summary')}>
                  <div><div className="text-gray-500">Toplam Oran</div><div className="gold-text font-bold text-sm mt-0.5">{Number(c.total_odd).toFixed(2)}</div></div>
                  <div><div className="text-gray-500">Miktar</div><div className="font-bold text-sm mt-0.5">{c.stake} ₺</div></div>
                  <div>
                    <div className="text-gray-500">{c.status === 'cashed_out' ? 'Ödenen' : 'Muhtemel'}</div>
                    <div className="gold-text font-bold text-sm mt-0.5">
                      {Number(c.status === 'cashed_out' || c.status === 'won' ? c.payout : c.possible_win).toFixed(2)} ₺
                    </div>
                  </div>
                </div>
                {c.status === 'active' && quote?.available && (
                  <button
                    onClick={() => acceptCashout(c)}
                    {...accountHook('cashout')}
                    disabled={busy[c.id]}
                    className="mt-3 w-full flex items-center justify-center gap-2 py-2 rounded font-bold text-sm bg-yellow-500/15 text-yellow-400 border border-yellow-500/40 hover:bg-yellow-500/25 disabled:opacity-50 transition-colors"
                  >
                    {busy[c.id]
                      ? <Loader2 size={14} className="animate-spin" />
                      : <Banknote size={14} />}
                    Cashout • {Number(quote.value).toFixed(2)} ₺
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BetCoupons;
